import { useCallback, useRef, useEffect } from 'react'
import axios from 'axios'
import { ValidationError, EncoderEvent } from '../components/Reader/Encoder/types'
import { constructRequestParams } from '../pages/Reader'

interface IWordLevelAnalysis {
    realisation: string
    code?: string
    error?: string
}

interface UseEtcbcValidationResult {
    validateValue: (value: string) => void
}

export function useEtcbcValidation(
    textEncoding: string,
    dispatch: React.Dispatch<EncoderEvent>,
    debounceMs = 300
): UseEtcbcValidationResult {
    const timeoutRef = useRef<NodeJS.Timeout | null>(null)
    const abortControllerRef = useRef<AbortController | null>(null)

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
            }
            if (abortControllerRef.current) {
                abortControllerRef.current.abort()
            }
        }
    }, [])

    const validateValue = useCallback(
        (value: string) => {
            // Clear previous timeout
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
            }

            // Cancel previous request
            if (abortControllerRef.current) {
                abortControllerRef.current.abort()
            }

            // Debounce the validation
            timeoutRef.current = setTimeout(async () => {
                abortControllerRef.current = new AbortController()

                try {
                    const data = constructRequestParams(textEncoding, [value])

                    const result = await axios.post(
                        'https://etcbc.vu.nl/api/wla',
                        data,
                        { signal: abortControllerRef.current.signal }
                    )

                    const errors: ValidationError[] = []

                    // Extract errors from the response
                    result.data.result[0]?.forEach(
                        (wordGroup: IWordLevelAnalysis[]) => {
                            wordGroup.forEach((token) => {
                                if (token.error) {
                                    errors.push({
                                        code: token.code || '',
                                        error: token.error,
                                    })
                                }
                            })
                        }
                    )

                    dispatch({
                        type: 'VALIDATION_COMPLETED',
                        errors,
                    })
                } catch (error) {
                    // Ignore abort errors
                    if (axios.isCancel(error)) {
                        return
                    }
                    // For other errors, dispatch empty errors (validation failed silently)
                    dispatch({
                        type: 'VALIDATION_COMPLETED',
                        errors: [],
                    })
                }
            }, debounceMs)
        },
        [textEncoding, dispatch, debounceMs]
    )

    return { validateValue }
}
